/**
 * Applies table column filters to an array of row objects.
 * @param {Array} rows - Array of flat row objects
 * @param {Array} filters - Array of filter objects { fieldPath, operator, value }
 * @param {string} combinator - 'AND' or 'OR' (default: 'AND')
 * @returns {Array} Filtered rows
 */
export const applyTableFilters = (rows, filters, combinator) => {
    if (!filters || filters.length === 0 || !Array.isArray(rows)) {
        return rows;
    }

    // Support filters being passed as an object with a combinator property
    // e.g. { filters: [...], combinator: 'OR' } or just a plain array
    let actualFilters = filters;
    let actualCombinator = combinator || 'AND';

    if (!Array.isArray(filters) && typeof filters === 'object') {
        actualFilters = filters.filters || [];
        actualCombinator = filters.combinator || 'AND';
    }

    if (!actualFilters || actualFilters.length === 0) {
        return rows;
    }

    const matchFn = actualCombinator === 'OR' ? 'some' : 'every';

    return rows.filter(row => {
        return actualFilters[matchFn](filter => {
            const { fieldPath, operator, value } = filter;

            if (!fieldPath) return true;

            const itemValue = row[fieldPath];
            let rowValStr = itemValue !== undefined && itemValue !== null ? String(itemValue).toLowerCase() : '';
            let filterValStr = value !== undefined && value !== null ? String(value).toLowerCase() : '';
            const isNullOrEmpty = itemValue === undefined || itemValue === null || itemValue === '';

            switch (operator) {
                case 'equals':
                    return rowValStr === filterValStr;
                case 'not_equals':
                    return rowValStr !== filterValStr;
                case 'contains':
                    return rowValStr.includes(filterValStr);
                case 'not_contains':
                    return !rowValStr.includes(filterValStr);
                case 'starts_with':
                    return rowValStr.startsWith(filterValStr);
                case 'ends_with':
                    return rowValStr.endsWith(filterValStr);
                case 'is_empty':
                    return isNullOrEmpty;
                case 'is_not_empty':
                    return !isNullOrEmpty;
                case 'greater_than':
                    return parseFloat(itemValue) > parseFloat(value);
                case 'greater_than_or_equal':
                    return parseFloat(itemValue) >= parseFloat(value);
                case 'less_than':
                    return parseFloat(itemValue) < parseFloat(value);
                case 'less_than_or_equal':
                    return parseFloat(itemValue) <= parseFloat(value);
                default:
                    return true;
            }
        });
    });
};
/**
 * Applies sorting to an array of row objects.
 * @param {Array} rows - Array of flat row objects
 * @param {Object} sortConfig - { fieldPath, direction }
 * @returns {Array} Sorted rows
 */
export const applyTableSort = (rows, sortConfig) => {
    if (!sortConfig || !sortConfig.fieldPath || !Array.isArray(rows) || rows.length === 0) {
        return rows;
    }

    const { fieldPath, direction = 'asc' } = sortConfig;
    const modifier = direction === 'desc' ? -1 : 1;

    // Create a copy to avoid mutating original
    return [...rows].sort((a, b) => {
        let valA = a[fieldPath];
        let valB = b[fieldPath];

        // Handle null/undefined
        if (valA === undefined || valA === null) valA = '';
        if (valB === undefined || valB === null) valB = '';

        // Strategic alphanumeric sorting
        // 1. Check if both are numbers or numeric strings
        const numA = parseFloat(valA);
        const numB = parseFloat(valB);
        
        if (!isNaN(numA) && !isNaN(numB) && String(numA) === String(valA).trim() && String(numB) === String(valB).trim()) {
            return (numA - numB) * modifier;
        }

        // 2. Special handling for IP addresses
        const ipRegex = /^(\d{1,3}\.){3}\d{1,3}$/;
        if (ipRegex.test(String(valA)) && ipRegex.test(String(valB))) {
            const partsA = String(valA).split('.').map(Number);
            const partsB = String(valB).split('.').map(Number);
            for (let i = 0; i < 4; i++) {
                if (partsA[i] !== partsB[i]) {
                    return (partsA[i] - partsB[i]) * modifier;
                }
            }
            return 0;
        }

        // 3. Default string comparison
        const strA = String(valA).toLowerCase();
        const strB = String(valB).toLowerCase();

        if (strA < strB) return -1 * modifier;
        if (strA > strB) return 1 * modifier;
        return 0;
    });
};
